import { Icon } from 'common/icon';
import { useAuth } from 'context/auth';
import { Routes } from 'constants/routes';
import styles from './styles.module.css';

import { Button } from 'common/new-button';
import { LoginModal } from 'components/modal/login';
import { Anchor } from 'constants/anchors';
import { ICasino, IComplaint, INavItem, IQuestion } from 'constants/types';

import { useLoginModal } from 'hooks';
import { useMemo, useState } from 'react';

import clsx from 'clsx';
import { ComplaintModalCurrentCasino } from 'components/modal/complaint-current-casino';
import { Header } from 'components/slider/header';
import { Avatar } from 'components/template/publication/comment';
import dayjs from 'dayjs';
import { getCategoryIcon } from 'utils/tagColor';
import { NavItem } from '../casino-navigations';
import Link from 'next/link';

interface ComplaintItemProps {
  item: IQuestion | IComplaint;
  isLast?: boolean;
  category?: string;
  hideRepliesCount?: boolean;
  routeTo: 'complaint' | 'question';
  rounded: boolean;
}

export const ComplaintItem = ({
  item,
  isLast,
  category,
  routeTo,
  hideRepliesCount = false,
  rounded,
}: ComplaintItemProps) => {
  const navItem: INavItem = {
    icon: <Icon.MessageFourth width="14" height="14" />,
    text: `${item?.replies_count} replies`,
    anchor:
      routeTo === 'complaint'
        ? Routes.complaint(item?._id)
        : Routes.question(item?._id),
  };

  const timeAgo = useMemo(() => {
    const now = dayjs();
    const targetDate = dayjs(item?.createdAt);
    const diffInMinutes = now.diff(targetDate, 'minute');
    const diffInHours = now.diff(targetDate, 'hour');
    const diffInDays = now.diff(targetDate, 'day');

    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${'mins ago'}`;
    } else if (diffInHours < 24) {
      return `${diffInHours} ${'h ago'}`;
    } else {
      return `${diffInDays} ${'days ago'}`;
    }
  }, [item?.createdAt]);

  const categoryIcon = getCategoryIcon(category);

  return (
    <Link
      href={navItem.anchor}
      className={clsx(styles.complaint_item_container, {
        [styles.container_bottom_rounded]: isLast,
        [styles.container_rounded]: rounded,
      })}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.userInfo}>
            <Avatar
              src={item?.user?.imageUrl || ''}
              alt={'user-image-url'}
              width={30}
              height={30}
            />
            <span className={styles.username}>{item?.user?.username}</span>
            {category && (
              <>
                <div className={styles.divider} />
                <div className={styles.category_container}>
                  {categoryIcon && (
                    <span className={styles.categoryIcon}>{categoryIcon}</span>
                  )}
                  <div className={styles.category}>{category}</div>
                </div>
              </>
            )}
            <div className={styles.divider} />
            <span className={styles.timestamp}>{timeAgo}</span>
          </div>
        </div>
        <p className={styles.complaintText}>{item?.title}</p>
      </div>
      {!hideRepliesCount && (
        <NavItem
          item={navItem}
          className={styles.navItem}
          arrowOpacity={'1'}
          arrow
          variantPadding="big"
          asStatic
        />
      )}
    </Link>
  );
};

interface Props {
  casino: ICasino;
  complaints: IComplaint[];
  count?: number;
}

export const ComplaintSection = ({ casino, complaints, count }: Props) => {
  const [isModalVisible, setModalVisible] = useState<boolean>(false);
  const { isLoggedIn } = useAuth();
  const {
    handleCloseLoginModal,
    handleConfirmLoginModal,
    handleVisibleLoginModal,
    isLoginVisible,
  } = useLoginModal();

  if (!complaints) return <></>;

  return (
    <>
      <div className={styles.container}>
        <Header name={`${casino.name} complaints`} count={count} isSmallName />
        <div id={Anchor.CasinoComplaints} className={styles.card}>
          <div className={styles.cardContent}>
            <Icon.VeryfiedThird />
            <div className={styles.textContent}>
              <p className={styles.description}>
                {
                  'Write a complaint and we will add the casino to the blacklist.\nWe will contact the owners of the casino and try to solve your problem.'
                }
              </p>
            </div>

            <div className={styles.actionSection}>
              <div className={styles.buttonWrapper}>
                <Button
                  icon={
                    <Icon.MessageThird fill="white" width="16" height="16" />
                  }
                  className={styles.button}
                  text={'Write Complaint'}
                  onClick={() =>
                    isLoggedIn
                      ? setModalVisible(true)
                      : handleVisibleLoginModal()
                  }
                />
              </div>
            </div>
          </div>
          <ComplaintModalCurrentCasino
            casino_id={casino._id}
            isVisible={isModalVisible}
            setVisible={setModalVisible}
          />
          <LoginModal
            isVisible={isLoginVisible}
            onClose={handleCloseLoginModal}
            confirmOnClick={handleConfirmLoginModal}
          />
        </div>
      </div>
      <div className={styles.complaintList}>
        {complaints?.map((complaint, index) => (
          <ComplaintItem
            key={index}
            item={complaint}
            isLast={complaints.length - 1 === index}
            routeTo={'complaint'}
            rounded={false}
          />
        ))}
      </div>
    </>
  );
};
